import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../foundation/altzero_base/base/components/ui/select';
import { Input } from '../../../foundation/altzero_base/base/components/ui/input';
import {
  Plus,
  BarChart3,
  Grid3X3,
  MapPin,
  CheckCircle,
  AlertCircle,
  Clock,
  Zap,
  Wand2,
  Trash2,
  Building2,
  Calendar
} from 'lucide-react';
import { useToast } from '../../../foundation/altzero_base/base/hooks/use-toast';
import { createOrGetTender, saveOffers, saveTenderSites, saveSites, getUserOrganizationId, getOffers, deleteOffer, deleteTenderSite } from '../services/tenderApiService';
import { offersNeedRemapping, remapChangedAnything } from '../lib/offerSiteRemap';
import { groupSites, groupLabel, groupSiteCount } from '../lib/siteGroups';
import { getTenderGroupNames, saveTenderGroupName } from '../services/tenderWorkflowService';
import { createClient } from '../services/clientService';
import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import { calculateTenderCosts } from '../services/calculationApiService';
import { generateUUID } from '../utils/cryptoUtils';

import OfferComparison from './OfferComparison';
import OfferManagementCard from './OfferManagementCard';
import { WorkflowNavigation } from './WorkflowNavigation';

import { useDocumentForm } from '../contexts/DocumentFormContext';
import {
  Offer,
  OfferType,
  OfferMetrics,
  ViewMode,
  createDefaultOffer,
  CONTRACT_LENGTHS,
  ALL_CONTRACT_TERMS,
  getOfferContractTerms,
  getAvailableTermsForOffer,
  createEmptyEnvironmentalCharges,
  createEmptyNonEnergyCharges
} from '../types/offerTypes';
import { getActiveRetailers, getRetailers, type Retailer } from '../services/retailerService';
import { generateDemoOffers } from '../utils/demoDataGenerator';
import { formatTermLabel } from '../lib/termLabel';

interface OffersStepProps {
  onNext: () => void;
  onPrevious: () => void;
}

const OffersStep: React.FC<OffersStepProps> = ({ onNext, onPrevious }) => {
  const { clientData, setClientData, offers: contextOffers, setOffers, sites, setSites, projectInfo, tenderId, selectedSiteId, setSelectedSiteId, registerStepFlush } = useDocumentForm();
  const { toast } = useToast();
  // LOCAL STATE FOR OFFERS - Persists across navigation
  const [localOffers, setLocalOffers] = useState<Offer[]>([]);
  const hasLoadedRef = useRef(false); // Track if we've attempted to load
  const lastSavedOffersRef = useRef<string>(''); // Track last saved offers hash to prevent double-saving
  const hasUnsavedChangesRef = useRef(false); // Track if offers have been modified

  // State management
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [loadingRetailers, setLoadingRetailers] = useState(true);

  // Resizable panel state
  const [leftPanelWidth, setLeftPanelWidth] = useState(280);
  const [isResizing, setIsResizing] = useState(false);

  // Use local offers if available, otherwise context offers
  const offers = localOffers.length > 0 ? localOffers : contextOffers;

  // OfferManagementCard's field edits (rate/environmental/non-energy charges) go through
  // updateOfferRateData in DocumentFormContext, which only updates contextOffers — it has no
  // knowledge of this step's separate localOffers copy, which `offers` above prefers whenever
  // non-empty. Without this sync, an edited field never reaches localOffers, so it's silently
  // missing from both the hasChanges hash and the actual payload saveOffers() sends to the DB —
  // "Continue to Proposal" would save, but the save wouldn't contain the edit at all.
  useEffect(() => {
    if (contextOffers.length === 0) return;
    setLocalOffers(prev => {
      if (prev.length === 0) return prev;
      let changed = false;
      const next = prev.map(lo => {
        const co = contextOffers.find(c => c.id === lo.id);
        if (co && co.multiYearRateData !== lo.multiYearRateData) {
          changed = true;
          return { ...lo, multiYearRateData: co.multiYearRateData, updatedAt: co.updatedAt };
        }
        return lo;
      });
      return changed ? next : prev;
    });
  }, [contextOffers]);

  // SIMPLE APPROACH: Show ALL offers, filter by site only if site is selected
  const filteredOffers = selectedSiteId
    ? offers.filter(offer => offer.siteId === selectedSiteId)
    : offers;

  // Fetch retailers from database
  useEffect(() => {
    const fetchRetailers = async () => {
      try {
        setLoadingRetailers(true);
        const result = await getActiveRetailers();
        if (result.success && result.data) {
          setRetailers(result.data);
        } else {
          console.error('Failed to fetch retailers:', result.error);
          setRetailers([]);
          toast({
            title: 'Error',
            description: result.error || 'Failed to load retailers',
            variant: 'destructive',
          });
        }
      } catch (error) {
        console.error('Error fetching retailers:', error);
        setRetailers([]);
        toast({
          title: 'Error',
          description: 'Failed to load retailers',
          variant: 'destructive',
        });
      } finally {
        setLoadingRetailers(false);
      }
    };
    fetchRetailers();
  }, [toast]);

  // Auto-select first site if no site selected and offers exist
  useEffect(() => {
    if (sites.length > 0 && offers.length > 0) {
      if (!selectedSiteId) {
        // Auto-select the site of the first offer, or first site
        const firstOfferSiteId = offers[0]?.siteId;
        if (firstOfferSiteId && sites.find(s => s.id === firstOfferSiteId)) {
          setSelectedSiteId(firstOfferSiteId);
        } else {
          setSelectedSiteId(sites[0].id);
        }
      } else {
        // Validate existing selection
        const siteExists = sites.find(s => s.id === selectedSiteId);
        if (!siteExists) {
          const firstOfferSiteId = offers[0]?.siteId;
          if (firstOfferSiteId && sites.find(s => s.id === firstOfferSiteId)) {
            setSelectedSiteId(firstOfferSiteId);
          } else {
            setSelectedSiteId(sites[0].id);
          }
        }
      }
    }
  }, [sites, offers, selectedSiteId, setSelectedSiteId]);

  // Reset selected offer when switching sites
  useEffect(() => {
    setSelectedOfferId(null);
  }, [selectedSiteId]);

  // Auto-expand first offer when switching sites
  useEffect(() => {
    const currentSiteOffers = offers.filter(o => o.siteId === selectedSiteId);
    if (currentSiteOffers.length > 0 && !selectedOfferId) {
      setSelectedOfferId(currentSiteOffers[0].id);
    }
  }, [selectedSiteId, offers, selectedOfferId]);

  // Helper function to create hash of offers for change detection
  const createOffersHash = useCallback((offersToHash: Offer[]): string => {
    // Create a simple hash based on offer IDs and key fields, IGNORING updatedAt.
    // multiYearRateData MUST be included — it's how rate/environmental/non-energy charge edits
    // made in OfferManagementCard (via context's updateOfferRateData) actually reach here. That
    // path never touches this component's hasUnsavedChangesRef, so without multiYearRateData in
    // the hash, a pure field edit looked identical to "no changes" — Next skipped saving entirely
    // and Compare & Select kept showing the stale, pre-edit DB row.
    const hashData = offersToHash.map(o => {
      return {
        id: o.id,
        siteId: o.siteId,
        retailerId: o.retailerId,
        termMonths: o.termMonths,
        recommendedMonths: o.recommendedMonths || [],
        multiYearRateData: o.multiYearRateData,
        // Explicitly exclude updatedAt to prevent save loops
      };
    });
    return JSON.stringify(hashData);
  }, []);

  // Helper function to map offers with correct siteIds (memoized)
  const mapOffersWithSites = useCallback((offersToMap: Offer[]): Offer[] => {
    if (sites.length === 0) {
      console.warn('⚠️ OffersStep: Cannot map offers - sites not loaded yet');
      return offersToMap; // Return as-is if sites not available
    }

    return offersToMap.map((dbOffer: any) => {
      // dbOffer.siteId is the database site_id from tender_site
      // We need to match it with frontend site.id

      // Strategy 1: Try to match database site_id with frontend site.id (if they're the same)
      const matchingSiteById = sites.find(s => s.id === dbOffer.siteId);
      if (matchingSiteById) {
        return {
          ...dbOffer,
          siteId: matchingSiteById.id,
          siteName: matchingSiteById.name || dbOffer.siteName,
        };
      }

      // Strategy 2: Try to match by site name (fallback) — only when unambiguous.
      // Two sites sharing a name would otherwise match the first one found, scrambling
      // the offer onto the wrong site. Ambiguous match falls through to Strategy 3.
      const sitesWithMatchingName = sites.filter(s => s.name === dbOffer.siteName);
      if (sitesWithMatchingName.length === 1) {
        const matchingSiteByName = sitesWithMatchingName[0];
        console.log(`📌 OffersStep: Matched offer site by name: ${dbOffer.siteName} -> ${matchingSiteByName.id}`);
        return {
          ...dbOffer,
          siteId: matchingSiteByName.id,
          siteName: matchingSiteByName.name,
        };
      }
      if (sitesWithMatchingName.length > 1) {
        console.warn(`⚠️ OffersStep: Ambiguous site name "${dbOffer.siteName}" matches ${sitesWithMatchingName.length} sites for offer ${dbOffer.id} — refusing to guess, keeping original siteId.`);
      }

      // Strategy 3: If no match, keep original but log warning
      console.warn(`⚠️ OffersStep: Could not match site for offer ${dbOffer.id} (siteId: ${dbOffer.siteId}, siteName: ${dbOffer.siteName})`);
      return dbOffer;
    });
  }, [sites]);

  // Reset load flag when tenderId changes
  useEffect(() => {
    hasLoadedRef.current = false;
  }, [tenderId]);

  // CRITICAL: Load offers from DB - ONLY when sites are available
  useEffect(() => {
    const loadOffers = async () => {
      // CRITICAL: Wait for sites to be loaded before attempting to load offers
      if (sites.length === 0) {
        console.log('⏳ OffersStep: Waiting for sites to load...');
        return;
      }

      // Check staged offers first (from EmailProcessingStep)
      const stagedOffersListStr = sessionStorage.getItem('scopingai_staged_offers_list');
      if (stagedOffersListStr && !hasLoadedRef.current) {
        try {
          const stagedOffers = JSON.parse(stagedOffersListStr);
          if (Array.isArray(stagedOffers) && stagedOffers.length > 0) {
            console.log('📋 OffersStep: Found staged offers');
            // Map staged offers with sites
            const mappedStagedOffers = mapOffersWithSites(stagedOffers);
            setLocalOffers(mappedStagedOffers);
            setOffers(mappedStagedOffers);
            sessionStorage.removeItem('scopingai_staged_offers_list');
            hasLoadedRef.current = true;
            return;
          }
        } catch (e) {
          // Continue to load from DB
        }
      }

      // Only load from DB if tenderId exists
      if (!tenderId) return;

      // Prevent multiple simultaneous loads
      if (hasLoadedRef.current) return;

      try {
        hasLoadedRef.current = true;
        setIsLoading(true);
        console.log('🔄 OffersStep: Loading offers from DB');
        const result = await getOffers(tenderId);

        if (result.success && result.offers && result.offers.length > 0) {
          // Map offers with correct siteIds now that sites are available
          const mappedOffers = mapOffersWithSites(result.offers);

          console.log(`✅ OffersStep: Loaded ${mappedOffers.length} offers from DB`);
          setLocalOffers(mappedOffers); // Store in LOCAL STATE
          setOffers(mappedOffers); // Also update context
          // Update hash to track current state
          lastSavedOffersRef.current = createOffersHash(mappedOffers);
          hasUnsavedChangesRef.current = false;
        } else {
          console.log('📭 OffersStep: No offers found in DB');
          lastSavedOffersRef.current = '';
          hasUnsavedChangesRef.current = false;
        }
      } catch (error) {
        console.error('❌ OffersStep: Error loading offers:', error);
        hasLoadedRef.current = false; // Reset on error so we can retry
      } finally {
        setIsLoading(false);
      }
    };

    loadOffers();
  }, [tenderId, sites.length, setOffers, mapOffersWithSites, createOffersHash]); // Depend on sites.length to wait for sites

  // ⚠️ THIS EFFECT USED TO LOCK THE PAGE UP. See lib/offerSiteRemap.ts for the full account.
  //
  // It read localOffers, wrote localOffers, and listed localOffers as its own dependency. That
  // only terminates while every pass makes progress, and it does not when an offer's site cannot
  // be matched at all: mapOffersWithSites hands such an offer back unchanged, so the condition
  // stayed true while setLocalOffers kept handing the effect a brand new array — "Maximum update
  // depth exceeded", and the error boundary took Stage 4 down. One unmatchable offer was enough,
  // and arriving from Stage 1 is how you get one, since that step rebuilds sites from the form.
  //
  // Two changes: the current offers are read through a ref, so writing them cannot re-trigger
  // this; and the result is only stored when the re-map actually moved something.
  const localOffersRef = useRef(localOffers);
  useEffect(() => { localOffersRef.current = localOffers; });

  // CRITICAL: Re-map offers when sites become available (if offers already loaded)
  useEffect(() => {
    const current = localOffersRef.current;
    if (current.length === 0 || sites.length === 0) return;
    if (!offersNeedRemapping(current, sites.map(s => s.id))) return;

    const remappedOffers = mapOffersWithSites(current);
    if (!remapChangedAnything(current, remappedOffers)) {
      // Nothing could be matched. Storing this would change only the array's identity.
      console.warn('⚠️ OffersStep: offers still unmatched after re-map — leaving them as they are');
      return;
    }
    console.log('🔄 OffersStep: Re-mapping offers with newly loaded sites');
    setLocalOffers(remappedOffers);
    setOffers(remappedOffers);
  }, [sites, setOffers, mapOffersWithSites]); // Re-run when sites load

  // Check for staged offer data from EmailProcessingStep (legacy)
  useEffect(() => {
    const stagedDataStr = sessionStorage.getItem('scopingai_staged_offer_data');
    if (stagedDataStr && sites.length > 0 && retailers.length > 0) {
      try {
        const stagedData = JSON.parse(stagedDataStr);

        const offerId = generateUUID();
        const selectedSite = sites.find(s => s.id === stagedData.siteId);

        let dbRetailer = retailers.find(r =>
          r.name.toLowerCase().includes(stagedData.retailerId.toLowerCase()) ||
          stagedData.retailerId.toLowerCase().includes(r.name.toLowerCase())
        );

        if (!dbRetailer && retailers.length > 0) {
          dbRetailer = retailers[0];
          console.warn(`⚠️ No retailer match found for ${stagedData.retailerId}, using fallback: ${dbRetailer.name}`);
        }

        const newOffer: Offer = {
          id: offerId,
          orgId: '', // Will be set by backend
          tenderId: tenderId || '',
          siteId: stagedData.siteId,
          siteName: selectedSite?.name || '',
          retailerId: dbRetailer?.id || retailers[0]?.id,
          retailerName: dbRetailer?.name || 'Unknown Retailer',
          offerType: 'electricity',
          termMonths: stagedData.termMonths,
          greenPercent: 0,
          feesJson: {
            environmentalCharges: createEmptyEnvironmentalCharges(),
            nonEnergyCharges: createEmptyNonEnergyCharges()
          },
          sourceFileId: 'Manual Entry',
          confidence: 100,
          isRecommended: false,
          recommendedMonths: [],
          offerLines: [],
          createdAt: new Date(),
          updatedAt: new Date(),
          status: 'draft',
          notes: 'Created from staged data'
        };

        const updatedOffers = [...offers, newOffer];
        setLocalOffers(updatedOffers);
        setOffers(updatedOffers);
        sessionStorage.removeItem('scopingai_staged_offer_data');
      } catch (error) {
        console.error('Error processing staged offer data:', error);
      }
    }
  }, [sites, retailers, offers, setOffers]);

  const handleMouseMove = React.useCallback((e: MouseEvent) => {
    if (!isResizing) return;

    e.preventDefault();
    const containerRect = document.querySelector('.offers-container')?.getBoundingClientRect();
    if (!containerRect) return;

    const newWidth = e.clientX - containerRect.left;
    const minWidth = 200;
    const maxWidth = Math.min(500, containerRect.width * 0.6);

    if (newWidth >= minWidth && newWidth <= maxWidth) {
      setLeftPanelWidth(newWidth);
    }
  }, [isResizing]);

  const handleMouseUp = React.useCallback(() => {
    setIsResizing(false);
  }, []);

  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove, { passive: false });
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.body.style.pointerEvents = 'none';

      const resizer = document.querySelector('.resizer-handle');
      if (resizer) {
        (resizer as HTMLElement).style.pointerEvents = 'auto';
      }
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const calculateMetrics = (): OfferMetrics => {
    const offersToUse = filteredOffers;
    const totalOffers = offersToUse.length;
    const pendingReview = offersToUse.filter(o => o.status === 'draft').length;
    const recommendedOffer = offersToUse.find(o => o.isRecommended);
    const validOffers = offersToUse.filter(o => o.estimatedAnnualCost);

    const averageAnnualCost = validOffers.length > 0
      ? validOffers.reduce((sum, o) => sum + (o.estimatedAnnualCost || 0), 0) / validOffers.length
      : 0;

    const averageSavings = validOffers.length > 0
      ? validOffers.reduce((sum, o) => sum + (o.estimatedSavingsPercent || 0), 0) / validOffers.length
      : 0;

    const bestOffer = validOffers.reduce((best, current) => {
      if (!best) return current;
      const bestCost = best.estimatedAnnualCost || Infinity;
      const currentCost = current.estimatedAnnualCost || Infinity;
      return currentCost < bestCost ? current : best;
    }, null as Offer | null);

    const processingCount = offersToUse.filter(o => o.status === 'draft').length;

    return {
      totalOffers,
      pendingReview,
      recommendedOffer: recommendedOffer || undefined,
      averageAnnualCost,
      averageSavings,
      averageSavingsPercent: averageSavings,
      bestOffer: bestOffer || undefined,
      processingCount,
    };
  };

  const handleAddOffer = () => {
    if (!selectedSiteId) {
      toast({
        title: "No Site Selected",
        description: "Please select a site before adding an offer.",
        variant: "destructive",
      });
      return;
    }

    const offerId = generateUUID();
    const selectedSite = sites.find(s => s.id === selectedSiteId);

    const newOffer: Offer = {
      id: offerId,
      orgId: '', // Will be set by backend
      tenderId: tenderId || '',
      siteId: selectedSiteId,
      siteName: selectedSite?.name || '',
      retailerId: '',
      retailerName: '',
      offerType: 'electricity',
      termMonths: 12,
      greenPercent: 0,
      confidence: 0,
      isRecommended: false,
      recommendedMonths: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      status: 'draft',
      offerLines: [],
      feesJson: {
        environmentalCharges: createEmptyEnvironmentalCharges(),
        nonEnergyCharges: createEmptyNonEnergyCharges()
      }
    };

    const updatedOffers = [...offers, newOffer];
    setLocalOffers(updatedOffers); // Update LOCAL STATE
    setOffers(updatedOffers); // Also update context
    setExpandedCards(prev => new Set([...prev, newOffer.id]));
    setSelectedOfferId(newOffer.id);
    hasUnsavedChangesRef.current = true; // Mark as changed
  };

  const handleUpdateOffer = (updatedOffer: Offer) => {
    const originalOffer = offers.find(o => o.id === updatedOffer.id);
    if (originalOffer && !updatedOffer.siteId) {
      updatedOffer.siteId = originalOffer.siteId;
      updatedOffer.siteName = originalOffer.siteName;
    }

    const clearedOffers: string[] = [];
    let updatedOffers = offers.map(offer => {
      if (offer.id === updatedOffer.id) {
        return updatedOffer;
      }

      if (updatedOffer.recommendedMonths && updatedOffer.recommendedMonths.length > 0 &&
        offer.siteId === updatedOffer.siteId && offer.siteId &&
        offer.recommendedMonths && offer.recommendedMonths.length > 0) {
        clearedOffers.push(offer.retailerName || 'Unknown Retailer');
        return {
          ...offer,
          recommendedMonths: [],
          updatedAt: new Date()
        };
      }

      return offer;
    });

    if (clearedOffers.length > 0) {
      toast({
        title: "Recommendation Updated",
        description: `Only one offer per site can be recommended. Cleared recommendations from: ${clearedOffers.join(', ')}`,
        duration: 5000,
      });
    }

    setLocalOffers(updatedOffers); // Update LOCAL STATE
    setOffers(updatedOffers); // Also update context
  };

  /**
   * Persists this step's offers (sites -> tender_sites -> offers -> recalculation).
   *
   * `advance` exists so the top stepper can reuse the exact same save path without also
   * moving the user forward: clicking another step must not lose unsaved offer edits, but it
   * must not hijack where the user asked to go either. Every early return that used to call
   * onNext() unconditionally now only advances when the caller is the "Next" button.
   */
  const persistOffers = async (advance: boolean): Promise<boolean> => {
    // Check if offers have changed since last save
    const currentHash = createOffersHash(offers);
    const hasChanges = currentHash !== lastSavedOffersRef.current || hasUnsavedChangesRef.current;

    // If no changes and we have a tenderId, skip saving and just proceed
    if (!hasChanges && tenderId && offers.length > 0) {
      console.log('✅ OffersStep: No changes detected, skipping save');
      if (advance) onNext();
      return true;
    }

    if (offers.length > 0) {
      setIsSaving(true);
      try {
        const orgId = await getUserOrganizationId();
        if (!orgId) {
          toast({
            title: "Warning",
            description: "Could not find your organization. Offers will not be saved to database.",
            variant: "destructive",
          });
          if (advance) onNext();
          return true;
        }

        // clientData.id can be missing here even though the user is well past the Client
        // Info step: ClientInfoStep only auto-creates a new client as part of ITS OWN
        // "Next" click (see its handleNext) — that safety net never runs for a tender
        // resumed/edited starting from a LATER step (ClientInfoStep never mounts), or for
        // any tender whose saved step1.clientData was persisted without an id for other
        // historical reasons. Previously this just warned and silently skipped saving.
        let clientId = clientData.id;
        if (!clientId && tenderId) {
          // Resumed tender: tender.client_id is NOT NULL in the DB, so the real client
          // link already exists there — only the in-memory restored snapshot lost it.
          // Look it up instead of creating a new client, which would leave a harmless
          // but messy DUPLICATE client record behind (this tender's own client_id is
          // never touched by this function either way, so a wrong guess here can't
          // corrupt the tender's real relationship — but it can litter the clients list).
          const { data: tenderRow } = await supabase
            .from('tender')
            .select('client_id')
            .eq('id', tenderId)
            .single();
          if (tenderRow?.client_id) {
            clientId = tenderRow.client_id;
            setClientData({ ...clientData, id: clientId });
            console.log("✅ OffersStep: self-healed missing client — reused tender's existing client_id", clientId);
          }
        }
        if (!clientId && clientData.name?.trim()) {
          // No existing tender to look up (brand-new tender) — genuinely nothing to
          // reuse, so create the client now instead of blocking the user 3+ steps away
          // from the Client Info screen where "select a client" would make sense.
          const clientResult = await createClient({
            name: clientData.name,
            contact_name: clientData.contact_name,
            abn: clientData.abn,
            channel_partner: clientData.channel_partner,
            sf_account_id: clientData.sf_account_id,
            email: clientData.email,
            phone: clientData.phone,
            industry: clientData.industry || "Energy",
          });
          if (clientResult.success && clientResult.data) {
            clientId = clientResult.data.id;
            setClientData({ ...clientData, id: clientId });
            console.log("✅ OffersStep: self-healed missing client — created", clientId);
          }
        }

        if (!clientId) {
          toast({
            title: "Warning",
            description: "Please select or create a client first. Offers will not be saved.",
            variant: "destructive",
          });
          if (advance) onNext();
          return true;
        }

        // CRITICAL FIX: If context already has a tenderId (from URL), use it directly.
        // createOrGetTender queries by client_id and can return a DIFFERENT tender than
        // the one in the URL, causing tender_id mismatches between parsed_offer_document and offer.
        let tenderResult: { success: boolean; tenderId?: string; error?: string };
        if (tenderId) {
          console.log(`✅ OffersStep: Using context tenderId from URL: ${tenderId}`);
          tenderResult = { success: true, tenderId };
        } else {
          tenderResult = await createOrGetTender({
            orgId,
            clientId,
            title: projectInfo.title || `Tender for ${clientData.name || 'Client'}`,
            description: `Energy tender proposal for ${clientData.name || 'client'}`,
          });
        }

        if (!tenderResult.success || !tenderResult.tenderId) {
          toast({
            title: "Warning",
            description: `Could not create tender: ${tenderResult.error}`,
            variant: "destructive",
          });
          if (advance) onNext();
          return true;
        }

        // Collect ALL unique siteIds from offers to ensure we create tender_site records for all
        const offerSiteIds = new Set<string>();
        offers.forEach(offer => {
          if (offer.siteId) {
            offerSiteIds.add(offer.siteId);
          }
        });

        // Get all sites that are referenced by offers (plus all sites in context)
        const sitesToProcess = new Map<string, any>();

        // Add all sites from context
        sites.forEach(site => {
          if (site.id) {
            sitesToProcess.set(site.id, site);
          }
        });

        // Add any sites referenced by offers that might not be in context
        offerSiteIds.forEach(siteId => {
          if (!sitesToProcess.has(siteId)) {
            const siteFromOffer = sites.find(s => s.id === siteId);
            if (siteFromOffer) {
              sitesToProcess.set(siteId, siteFromOffer);
            }
          }
        });

        const sitesArray = Array.from(sitesToProcess.values());

        if (sitesArray.length === 0) {
          toast({
            title: "Error",
            description: "No sites found. Please add sites before creating offers.",
            variant: "destructive",
          });
          setIsSaving(false);
          return false;
        }

        // Ensure sites are saved to database first before creating tender_site records
        const sitesResult = await saveSites({
          orgId,
          clientId,
          sites: sitesArray,
        });

        if (!sitesResult.success || !sitesResult.savedSites) {
          toast({
            title: "Error",
            description: `Failed to save sites: ${sitesResult.error || 'Unknown error'}`,
            variant: "destructive",
          });
          setIsSaving(false);
          return false;
        }

        // Create a map of frontend site.id -> database site.id
        const siteIdMapping = new Map<string, string>();
        sitesArray.forEach(frontendSite => {
          // Prefer NMI — it's a unique key, so this match is always safe.
          const savedSiteByNmi = sitesResult.savedSites?.find(ss =>
            ss.nmi && frontendSite.nmi && ss.nmi === frontendSite.nmi
          );
          if (savedSiteByNmi) {
            siteIdMapping.set(frontendSite.id, savedSiteByNmi.id);
            console.log(`🔄 Mapped frontend site.id "${frontendSite.id}" -> database site.id "${savedSiteByNmi.id}" (by NMI)`);
            return;
          }

          // Fall back to name — only when NMI is unavailable on either side, and only if
          // the name uniquely identifies one saved site. Two sites sharing a name with no
          // NMI would otherwise map to the first match found, misattributing tender_site rows.
          const nameCandidates = (sitesResult.savedSites || []).filter(ss =>
            ss.name && frontendSite.name && ss.name === frontendSite.name && (!frontendSite.nmi || !ss.nmi)
          );
          if (nameCandidates.length === 1) {
            siteIdMapping.set(frontendSite.id, nameCandidates[0].id);
            console.log(`🔄 Mapped frontend site.id "${frontendSite.id}" -> database site.id "${nameCandidates[0].id}" (by name)`);
          } else if (nameCandidates.length > 1) {
            console.warn(`⚠️ OffersStep: Ambiguous site name "${frontendSite.name}" matches ${nameCandidates.length} saved sites with no NMI to disambiguate — skipping mapping for frontend site "${frontendSite.id}".`);
          }
        });

        // Update sites with database IDs for saving tender_site records
        const updatedSites = sitesArray.map(site => {
          const dbSiteId = siteIdMapping.get(site.id);
          return dbSiteId ? { ...site, id: dbSiteId } : site;
        });

        // Create reverse mapping: database site.id -> frontend site.id
        // This is needed so saveTenderSites can map back to frontend IDs
        const dbToFrontendSiteMap = new Map<string, string>();
        siteIdMapping.forEach((dbId, frontendId) => {
          dbToFrontendSiteMap.set(dbId, frontendId);
        });

        // Create tender_site records for all sites using database IDs
        // Pass the reverse mapping so saveTenderSites can create proper frontend ID -> tender_site.id mapping
        const tenderSitesResult = await saveTenderSites({
          orgId,
          tenderId: tenderResult.tenderId,
          sites: updatedSites, // These have database IDs now
          originalSiteIds: dbToFrontendSiteMap, // Maps db ID -> frontend ID
        });

        if (!tenderSitesResult.success || !tenderSitesResult.siteIdMap) {
          toast({
            title: "Error",
            description: `Could not create tender-site records: ${tenderSitesResult.error || 'Unknown error'}`,
            variant: "destructive",
          });
          setIsSaving(false);
          return false;
        }

        // Debug logging
        console.log('🔍 DEBUG: Validating offers against siteIdMap');
        console.log(`   - Total offers: ${offers.length}`);
        console.log(`   - SiteIdMap size: ${tenderSitesResult.siteIdMap.size}`);
        console.log(`   - SiteIdMap keys:`, Array.from(tenderSitesResult.siteIdMap.keys()));
        offers.forEach((offer, idx) => {
          console.log(`   - Offer ${idx + 1}: siteId="${offer.siteId}", found=${tenderSitesResult.siteIdMap?.has(offer.siteId || '')}`);
        });

        // Validate that all offers have valid siteIds
        const offersWithInvalidSites = offers.filter(offer => {
          if (!offer.siteId) {
            console.warn(`⚠️ Offer ${offer.id} has no siteId`);
            return true;
          }
          const tenderSiteId = tenderSitesResult.siteIdMap?.get(offer.siteId);
          if (!tenderSiteId) {
            console.warn(`⚠️ Offer ${offer.id} has siteId "${offer.siteId}" but not found in siteIdMap`);
          }
          return !tenderSiteId;
        });

        if (offersWithInvalidSites.length > 0) {
          const missingSiteIds = offersWithInvalidSites.map(o => o.siteId || 'missing').join(', ');
          console.error('❌ Offers with invalid siteIds:', offersWithInvalidSites.map(o => ({ id: o.id, siteId: o.siteId })));
          console.error('📋 Available siteIds in map:', Array.from(tenderSitesResult.siteIdMap.keys()));
          console.error('📋 All offer siteIds:', offers.map(o => o.siteId));
          toast({
            title: "Error",
            description: `${offersWithInvalidSites.length} offer(s) have invalid site assignments. Missing siteIds: ${missingSiteIds}. Please ensure all offers are assigned to valid sites.`,
            variant: "destructive",
          });
          setIsSaving(false);
          return false;
        }

        // Continue with offers saving using the siteIdMap (which maps frontend site.id -> tender_site.id)
        const result = await saveOffers({
          orgId,
          clientId,
          tenderId: tenderResult.tenderId,
          offers,
          siteIdMap: tenderSitesResult.siteIdMap,
        });

        if (result.success) {
          // Reload offers from database and update LOCAL STATE
          console.log('🔄 Reloading offers from database after save...');
          const reloadResult = await getOffers(tenderResult.tenderId);

          if (reloadResult.success && reloadResult.offers && reloadResult.offers.length > 0) {
            const mappedOffers = reloadResult.offers.map((dbOffer: any) => {
              const matchingSite = sites.find(s => s.id === dbOffer.siteId);
              if (matchingSite) {
                return {
                  ...dbOffer,
                  siteId: matchingSite.id,
                  siteName: matchingSite.name || dbOffer.siteName,
                };
              }
              return dbOffer;
            });

            console.log(`✅ Reloaded ${mappedOffers.length} offers from database`);
            setLocalOffers(mappedOffers); // Update LOCAL STATE
            setOffers(mappedOffers); // Also update context
            // Update hash after successful save
            lastSavedOffersRef.current = createOffersHash(mappedOffers);
            hasUnsavedChangesRef.current = false;
          }

          // TRIGGER CALCULATION AFTER STEP 2 - Calculate costs for all offers
          console.log('🧮 Triggering calculation for tender:', tenderResult.tenderId);
          try {
            const calcResult = await calculateTenderCosts(tenderResult.tenderId);
            if (calcResult.success) {
              console.log(`✅ Calculation complete: ${calcResult.calculated} offers calculated`);
              toast({
                title: "Success",
                description: `Offers saved and ${calcResult.calculated} offer costs calculated successfully`,
              });
            } else {
              console.warn('⚠️ Calculation had errors:', calcResult.errors);
              toast({
                title: "Offers Saved",
                description: `Offers saved. Some calculations had errors: ${calcResult.errors.length} failed`,
                variant: "default",
              });
            }
          } catch (calcError) {
            console.error('⚠️ Calculation failed:', calcError);
            // Don't block navigation if calculation fails - offers are already saved
            toast({
              title: "Offers Saved",
              description: "Offers saved successfully. Cost calculations will be available shortly.",
            });
          }

          if (advance) onNext();
          return true;
        } else {
          toast({
            title: "Error",
            description: result.error || "Failed to save offers",
            variant: "destructive",
          });
          return false;
        }
      } catch (error) {
        console.error("Error saving offers:", error);
        toast({
          title: "Error",
          description: "An unexpected error occurred",
          variant: "destructive",
        });
        return false;
      } finally {
        setIsSaving(false);
      }
    } else {
      if (advance) onNext();
    }
    return true;
  };

  const handleSaveAndNext = async () => { await persistOffers(true); };

  // Keeps the flush handler stable (registered once) while always calling the LATEST closure —
  // persistOffers is recreated every render and closes over offers/clientData/sites.
  const persistOffersRef = useRef(persistOffers);
  // Updated in an effect, not during render: React 19 may discard a render, and writing a ref
  // in the render body is unsafe. Effects run on commit, so the ref is current well before any
  // click can reach it.
  useEffect(() => { persistOffersRef.current = persistOffers; });

  // Step 4's saveCurrentStepData payload is the in-memory offers array only; the DB write lives
  // in persistOffers, which used to run solely on "Next". A stepper click away from here would
  // otherwise drop every offer edit made since arriving.
  // goToStep's own in-flight guard only stops two stepper clicks racing each other. It cannot
  // see a save already running from the Next button, and the stepper stays clickable during one
  // — two concurrent persistOffers calls would double-write offers and then race onNext()
  // against setCurrentStep. Refusing here keeps the user on step 4 until the save settles.
  const isSavingRef = useRef(isSaving);
  useEffect(() => { isSavingRef.current = isSaving; });

  useEffect(() => registerStepFlush(4, async () => {
    if (isSavingRef.current) throw new Error('A save is already in progress');

    // persistOffers returns false exactly where the Next button refuses to advance (no sites,
    // site save failed, tender_site failed, invalid site assignments, offer save failed, threw).
    // goToStep only aborts on a THROWN flush, so a plain false has to be turned into one —
    // otherwise a stepper click would sail past an error Next would have stopped on, and the
    // offer edits would be gone with only a red toast behind them.
    const saved = await persistOffersRef.current(false);
    if (!saved) throw new Error('Offers could not be saved');
  }), [registerStepFlush]);


  const handleRemoveOffer = (offerId: string) => {
    const updatedOffers = offers.filter(offer => offer.id !== offerId);
    setLocalOffers(updatedOffers); // Update LOCAL STATE
    setOffers(updatedOffers); // Also update context
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      newSet.delete(offerId);
      return newSet;
    });
    hasUnsavedChangesRef.current = true; // Mark as changed

    // Permanently delete the DB row right away — saveOffers only upserts by id and never deletes
    // rows missing from its input, so without this the offer's row stayed in the DB untouched and
    // kept showing up in Compare & Select (which reads offers straight from the DB), even though
    // it had been "removed" here. No-op if this offer was never saved yet (brand new, local-only).
    deleteOffer(offerId).then(result => {
      if (!result.success) {
        console.error('❌ Failed to delete offer from database:', result.error);
        toast({
          title: 'Delete failed',
          description: `Offer removed here, but couldn't delete it from the database: ${result.error}`,
          variant: 'destructive',
        });
      }
    });
  };

  /**
   * Remove the site currently chosen in the picker above, without a trip back to Stage 1.
   *
   * The team meet an unpriced site HERE — this is the step where they find out a retailer did
   * not quote it — so this is where they should be able to drop it. Stage 1 keeps its own
   * remove button; this is an addition, not a move.
   *
   * ⚠️ The site's offers go with it. offer.tender_site_id is ON DELETE CASCADE and every offer
   * on prod carries one, so the database removes them whether or not this does; clearing them
   * from local state as well keeps the screen honest instead of showing rows whose site is gone.
   * Same confirmation wording as the Stage 1 button.
   */
  const handleRemoveSite = (siteId: string) => {
    const site = sites.find(s => s.id === siteId);
    if (!site) return;
    const label = site.name?.trim() || site.nmi || 'this site';
    const siteOffers = offers.filter(o => o.siteId === siteId);
    const alsoOffers = siteOffers.length
      ? ` Its ${siteOffers.length} offer${siteOffers.length === 1 ? '' : 's'} will be deleted too.`
      : '';
    if (!window.confirm(`Remove ${label}?${alsoOffers} This cannot be undone.`)) return;

    const remainingSites = sites.filter(s => s.id !== siteId);
    const remainingOffers = offers.filter(o => o.siteId !== siteId);
    setSites(remainingSites);
    setLocalOffers(remainingOffers);
    setOffers(remainingOffers);
    // Move the picker to a site that still exists, or clear it when none do.
    setSelectedSiteId(remainingSites[0]?.id ?? '');
    hasUnsavedChangesRef.current = true;

    if (!tenderId || !site.nmi) return;
    deleteTenderSite(tenderId, site.nmi).then(result => {
      if (!result.success) {
        console.error('❌ Failed to delete site from database:', result.error);
        toast({
          title: 'Delete failed',
          description: `Site removed here, but couldn't delete it from the database: ${result.error}`,
          variant: 'destructive',
        });
      }
    });
  };

  const handleCopyOffer = (offerId: string) => {
    const originalOffer = offers.find(o => o.id === offerId);
    if (!originalOffer) return;

    const newOfferId = generateUUID();
    const copiedOffer: Offer = {
      ...originalOffer,
      id: newOfferId,
      retailerName: `${originalOffer.retailerName} (Copy)`,
      isRecommended: false,
      recommendedMonths: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      offerLines: originalOffer.offerLines.map(line => ({
        ...line,
        id: generateUUID(),
        offerId: newOfferId,
      })),
    };

    const updatedOffers = [...offers, copiedOffer];
    setLocalOffers(updatedOffers); // Update LOCAL STATE
    setOffers(updatedOffers); // Also update context
    hasUnsavedChangesRef.current = true; // Mark as changed
  };

  const handleToggleExpansion = (offerId: string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(offerId)) {
        newSet.delete(offerId);
      } else {
        newSet.add(offerId);
      }
      return newSet;
    });
  };

  const handleOfferSelect = (offerId: string) => {
    handleToggleExpansion(offerId);
  };

  const handleCompareAll = () => {
    setViewMode('comparison');
  };

  const handlePopulateDemoData = async () => {
    if (sites.length === 0) {
      toast({
        title: "No Sites Available",
        description: "Please add sites in the previous step before generating demo offers.",
        variant: "destructive",
      });
      return;
    }

    try {
      const allRetailersResult = await getRetailers();

      if (!allRetailersResult.success || !allRetailersResult.data || allRetailersResult.data.length === 0) {
        toast({
          title: "No Retailers Available",
          description: "Please add retailers before generating demo offers.",
          variant: "destructive",
        });
        return;
      }

      const allRetailers = allRetailersResult.data;
      const formattedOffers: Offer[] = [];

      sites.forEach((site, siteIndex) => {
        allRetailers.forEach((retailer) => {
          ALL_CONTRACT_TERMS.forEach((termMonths) => {
            const offerId = generateUUID();
            const greenPercent = Math.floor(Math.random() * 100);

            const newOffer: Offer = {
              id: offerId,
              orgId: '',
              tenderId: '',
              tenderSiteId: undefined,
              siteId: site.id,
              siteName: site.name || `Site ${siteIndex + 1}`,
              retailerId: retailer.id,
              retailerName: retailer.name,
              retailerLogo: undefined,
              offerType: 'electricity' as OfferType,
              validFrom: undefined,
              validTo: undefined,
              termMonths: termMonths,
              greenPercent: greenPercent,
              feesJson: {
                environmentalCharges: createEmptyEnvironmentalCharges(),
                nonEnergyCharges: createEmptyNonEnergyCharges(),
                connectionFee: Math.round((Math.random() * 100 + 50) * 100) / 100,
                exitFee: Math.round((Math.random() * 200 + 100) * 100) / 100,
                latePaymentFee: Math.round((Math.random() * 50 + 20) * 100) / 100,
                latePaymentPercent: Math.round((Math.random() * 5 + 2) * 100) / 100,
                paperBillFee: Math.round((Math.random() * 10 + 5) * 100) / 100,
                dishonourFee: Math.round((Math.random() * 30 + 15) * 100) / 100,
              },
              sourceFileId: undefined,
              sourceCitations: undefined,
              confidence: 95,
              isRecommended: false,
              recommendedMonths: [],
              multiYearRateData: {
                smooth: {
                  peakRates: { year_1: 0.2, year_2: 0.22, year_3: 0.24, year_4: 0.26, year_5: 0.28, year_6: 0.30 },
                  offpeakRates: { year_1: 0.15, year_2: 0.16, year_3: 0.17, year_4: 0.18, year_5: 0.19, year_6: 0.20 },
                  shoulderRates: { year_1: 0.17, year_2: 0.18, year_3: 0.19, year_4: 0.20, year_5: 0.21, year_6: 0.22 },
                  environmentalCharges: createEmptyEnvironmentalCharges(),
                  nonEnergyCharges: createEmptyNonEnergyCharges()
                },
                stepped: {
                  peakRates: { year_1: 0.18, year_2: 0.25, year_3: 0.28, year_4: 0.30, year_5: 0.32, year_6: 0.34 },
                  offpeakRates: { year_1: 0.13, year_2: 0.18, year_3: 0.20, year_4: 0.22, year_5: 0.24, year_6: 0.26 },
                  shoulderRates: { year_1: 0.15, year_2: 0.20, year_3: 0.22, year_4: 0.24, year_5: 0.26, year_6: 0.28 },
                  environmentalCharges: createEmptyEnvironmentalCharges(),
                  nonEnergyCharges: createEmptyNonEnergyCharges()
                },
              },
              rationale: undefined,
              estimatedAnnualCost: Math.random() * 2000 + 1500,
              estimatedSavings: undefined,
              estimatedSavingsPercent: undefined,
              offerLines: [],
              createdAt: new Date(),
              updatedAt: new Date(),
              status: 'draft',
              notes: undefined,
              extn: undefined,
            };
            formattedOffers.push(newOffer);
          });
        });
      });

      setLocalOffers(formattedOffers); // Update LOCAL STATE
      setOffers(formattedOffers); // Also update context
      setExpandedCards(new Set(formattedOffers.map(offer => offer.id)));

      if (!selectedSiteId && sites.length > 0) {
        setSelectedSiteId(sites[0].id);
      }

      if (formattedOffers.length > 0) {
        setSelectedOfferId(formattedOffers[0].id);
      }

      toast({
        title: "Demo Data Generated",
        description: `Generated ${formattedOffers.length} demo offers.`,
      });
    } catch (error) {
      console.error('❌ Error generating demo offers:', error);
      toast({
        title: "Error",
        description: "Failed to generate demo offers.",
        variant: "destructive",
      });
    }
  };

  const isReadyForNext = () => {
    const hasOffers = offers.length > 0;
    const allOffersValid = offers.every(offer => {
      const hasRetailer = offer.retailerId !== null && offer.retailerId !== undefined;
      const hasContractTerm = (offer.termMonths && offer.termMonths > 0) ||
        (offer.extn?.contract_terms && Object.keys(offer.extn.contract_terms).length > 0) ||
        (offer.availableTerms && offer.availableTerms.length > 0);
      const hasOfferLines = offer.offerLines && offer.offerLines.length > 0;
      const hasValidRates = offer.offerLines?.some(line => line.rate !== undefined && line.rate > 0) || false;
      return hasRetailer && hasContractTerm && hasOfferLines && hasValidRates;
    });
    return hasOffers && allOffersValid;
  };

  /**
   * F5 — the Groups selector their mock-up asks for, beside the Sites one.
   *
   * A retailer prices a legal entity's sites in one state, coming off contract on one date, as a
   * single deal. That is the unit the team work in, so Stage 4 offers it: pick the group, then a
   * site within it, or all of them.
   *
   * The key is the same one the proposal groups by — see lib/siteGroups — so a group offered
   * here is a group the generated document actually prints.
   */
  const siteGroups = useMemo(() => groupSites(sites), [sites]);
  const [groupNames, setGroupNames] = useState<Record<string, string>>({});
  const [selectedGroupKey, setSelectedGroupKey] = useState<string>('');

  useEffect(() => {
    if (!tenderId) return;
    let mounted = true;
    getTenderGroupNames(tenderId).then(names => { if (mounted) setGroupNames(names); });
    return () => { mounted = false; };
  }, [tenderId]);

  // Keep the group in step with the site the rest of the step is already showing, so arriving
  // from another stage does not land on a group that does not hold the selected site.
  useEffect(() => {
    if (siteGroups.length === 0) return;
    const holdingSelected = siteGroups.find(g => g.sites.some(st => st.id === selectedSiteId));
    const next = holdingSelected?.key ?? siteGroups[0].key;
    if (next !== selectedGroupKey) setSelectedGroupKey(next);
  }, [siteGroups, selectedSiteId, selectedGroupKey]);

  const activeGroup = siteGroups.find(g => g.key === selectedGroupKey) ?? siteGroups[0] ?? null;
  // The Sites dropdown lists only this group's sites — picking a group narrows the step.
  const sitesInGroup = activeGroup?.sites ?? sites;

  const handleRenameGroup = async (groupKey: string, name: string) => {
    setGroupNames(prev => {
      const next = { ...prev };
      if (name.trim()) next[groupKey] = name.trim(); else delete next[groupKey];
      return next;
    });
    if (!tenderId) return;
    const result = await saveTenderGroupName(tenderId, groupKey, name);
    if (!result.success) {
      toast({
        title: 'Group name not saved',
        description: `${result.error || 'Unknown error'}. It will show the entity name instead.`,
        variant: 'destructive',
      });
    }
  };

  /** Offers and status for a whole group, for the line under its name in the dropdown. */
  const getGroupStats = (groupKey: string) => {
    const group = siteGroups.find(g => g.key === groupKey);
    const ids = new Set((group?.sites ?? []).map(st => st.id));
    const groupOffers = offers.filter(o => ids.has(String(o.siteId)));
    const hasRecommended = groupOffers.some(o => o.isRecommended);
    const status = hasRecommended ? 'complete' : groupOffers.length > 0 ? 'in-progress' : 'pending';
    let statusIcon = AlertCircle;
    if (status === 'complete') statusIcon = CheckCircle;
    else if (status === 'in-progress') statusIcon = Clock;
    return { totalOffers: groupOffers.length, status, statusIcon };
  };

  const getSiteStats = (siteId: string) => {
    const siteOffers = offers.filter(o => o.siteId === siteId);
    const totalOffers = siteOffers.length;
    const hasRecommended = siteOffers.some(o => o.isRecommended);
    const status = hasRecommended ? 'complete' : totalOffers > 0 ? 'in-progress' : 'pending';

    let statusIcon = AlertCircle;
    if (status === 'complete') statusIcon = CheckCircle;
    else if (status === 'in-progress') statusIcon = Clock;

    return { totalOffers, status, statusIcon };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsResizing(true);
    e.preventDefault();
  };

  const handleContractTermChange = (offerId: string, value: string) => {
    const termMonths = parseInt(value);
    if (isNaN(termMonths)) return;

    const offerToUpdate = offers.find(o => o.id === offerId);
    if (!offerToUpdate) return;

    const updatedOffer = {
      ...offerToUpdate,
      termMonths: termMonths,
      updatedAt: new Date()
    };
    handleUpdateOffer(updatedOffer);
  };

  const isProposalReady = isReadyForNext();

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Energy Offers</h2>
          <p className="text-muted-foreground mt-1">
            Compare retailer offers and select the best option for {clientData.name}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <Button
            onClick={handlePopulateDemoData}
            variant="outline"
            className="border-purple-200 text-purple-700 hover:bg-purple-50 hover:border-purple-300"
            size="sm"
          >
            <Wand2 className="w-4 h-4 mr-2" />
            Populate Demo Values
          </Button>
        </div>
      </div>

      {/*
        F5 — Groups, beside Sites, as their mock-up asks.

        A retailer prices a legal entity's sites in one state, coming off contract on one date,
        as a single deal; that is the unit the team work in. Picking a group narrows the Sites
        list below to that group's own sites.

        Shown only when there is more than one group — a tender with one deal has nothing to
        pick between, and the row would only be in the way.
      */}
      {siteGroups.length > 1 && (
        <div className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-gray-700">Group:</span>
          </div>

          <div className="flex-1">
            <Select
              value={selectedGroupKey}
              onValueChange={(key) => {
                setSelectedGroupKey(key);
                // Move the site selection into the chosen group, or the step below would still
                // be showing a site the group does not hold.
                const group = siteGroups.find(g => g.key === key);
                if (group?.sites[0]) setSelectedSiteId(group.sites[0].id);
              }}
            >
              <SelectTrigger className="w-full max-w-md h-9 bg-white border border-gray-300 hover:border-primary/50 transition-colors">
                <SelectValue placeholder="Choose a group" />
              </SelectTrigger>
              <SelectContent>
                {siteGroups.map((group) => {
                  const stats = getGroupStats(group.key);
                  const StatusIcon = stats.statusIcon;
                  return (
                    <SelectItem key={group.key} value={group.key} className="cursor-pointer">
                      <div className="flex items-center gap-2 w-full">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{groupLabel(group, groupNames)}</div>
                          <div className="text-xs text-muted-foreground">
                            {groupSiteCount(group)} &middot; NMI: {group.nmis.join(', ') || '—'}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-medium">
                            {stats.totalOffers}
                          </span>
                          <StatusIcon className={`w-3 h-3 ${stats.status === 'complete' ? 'text-green-600' :
                            stats.status === 'in-progress' ? 'text-blue-600' : 'text-gray-400'
                            }`} />
                        </div>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* F1 — the team name their own groups; blank falls back to the entity name. */}
          {activeGroup && (
            <Input
              value={groupNames[activeGroup.key] ?? ''}
              onChange={(e) => setGroupNames(prev => ({ ...prev, [activeGroup.key]: e.target.value }))}
              onBlur={(e) => handleRenameGroup(activeGroup.key, e.target.value)}
              placeholder={activeGroup.entityName || 'Name this group'}
              className="h-9 w-56 shrink-0"
              title="Name this group"
            />
          )}
        </div>
      )}

      {/* Compact Site Selection */}
      <div className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium text-gray-700">Site:</span>
        </div>

        {sites.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            No sites available. Please add sites in the previous step.
          </div>
        ) : (
          <div className="flex-1">
            <Select value={selectedSiteId} onValueChange={setSelectedSiteId}>
              <SelectTrigger className="w-full max-w-md h-9 bg-white border border-gray-300 hover:border-primary/50 transition-colors">
                <SelectValue placeholder="Choose a site">
                  {selectedSiteId && (() => {
                    const selectedSite = sites.find(s => s.id === selectedSiteId);
                    const stats = getSiteStats(selectedSiteId);
                    const StatusIcon = stats.statusIcon;

                    return selectedSite ? (
                      <div className="flex items-center gap-2 w-full">
                        <span className="font-medium text-gray-900 truncate">{selectedSite.name}</span>
                        <div className="ml-auto flex items-center gap-1">
                          <span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
                            {stats.totalOffers}
                          </span>
                          <StatusIcon className={`w-3 h-3 ${stats.status === 'complete' ? 'text-green-600' :
                            stats.status === 'in-progress' ? 'text-blue-600' : 'text-gray-400'
                            }`} />
                        </div>
                      </div>
                    ) : null;
                  })()}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {sitesInGroup.map((site) => {
                  const stats = getSiteStats(site.id);
                  const StatusIcon = stats.statusIcon;

                  return (
                    <SelectItem key={site.id} value={site.id} className="cursor-pointer">
                      <div className="flex items-center gap-2 w-full">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{site.name}</div>
                          <div className="text-xs text-muted-foreground">NMI: {site.nmi}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-medium">
                            {stats.totalOffers}
                          </span>
                          <StatusIcon className={`w-3 h-3 ${stats.status === 'complete' ? 'text-green-600' :
                            stats.status === 'in-progress' ? 'text-blue-600' : 'text-gray-400'
                            }`} />
                        </div>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Drop a site the retailers never priced, without going back to Stage 1. */}
        {sites.length > 1 && selectedSiteId && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleRemoveSite(selectedSiteId)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 shrink-0"
            title="Remove this site from the tender"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Remove site
          </Button>
        )}
      </div>

      {/* Main Content - Master-Detail Layout */}
      <div className="space-y-6">
        {viewMode === 'comparison' && filteredOffers.length >= 2 ? (
          <OfferComparison
            offers={filteredOffers}
            onOfferSelect={handleOfferSelect}
          />
        ) : (
          /* Enhanced Resizable Master-Detail Layout */
          <div className="offers-container flex min-h-[600px] bg-gray-50 rounded-lg overflow-hidden relative">
            {/* Master Panel - Offers List (Resizable) */}
            <div
              className="bg-white border-r border-gray-200 flex flex-col"
              style={{ width: `${leftPanelWidth}px`, minWidth: '200px', maxWidth: '500px' }}
            >
              <div className="p-4 space-y-3 flex-1">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold text-gray-900">
                    Offers ({filteredOffers.length})
                  </h3>
                  <Button
                    onClick={handleAddOffer}
                    size="sm"
                    variant="outline"
                    className="text-xs"
                    disabled={!selectedSiteId}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add
                  </Button>
                </div>

                <div className="space-y-1 flex-1 overflow-y-auto pr-1">
                  {filteredOffers.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <div className="text-sm">No offers found for this site</div>
                      <div className="text-xs mt-1">Click "Add" to create your first offer</div>
                    </div>
                  ) : (
                    filteredOffers.map((offer) => {
                      const retailer = Array.isArray(retailers) ? retailers.find(r => r.id === offer.retailerId) : undefined;
                      const isSelected = selectedOfferId === offer.id;

                      const displayName = retailer?.name || offer.retailerName || 'Unknown Retailer';
                      const displayLogo = retailer?.logo || offer.retailerLogo;
                      const termOptions = getAvailableTermsForOffer(offer);
                      // THIS offer's own term -> year assignment, never the tender-wide merge.
                      //
                      // ⚠️ A per-offer editor must label from the offer in front of the user.
                      // The merge is right for Compare & Select, which ranks every retailer under
                      // one set of year columns, and wrong here: Origin assigning 16 -> Year 2
                      // (it also quotes a 4-month stub) relabelled FLO's 16-month term, which has
                      // no stub and is genuinely its Year 1. Flo lost Year 1 entirely without
                      // anyone having touched it.
                      //
                      // {} rather than null on purpose — an empty assignment means "derive", where
                      // null would fall back to the tender merge and reintroduce the bleed.
                      const offerTermYears = (offer as any)?.multiYearRateData?.term_year_labels
                        ?? (offer as any)?.extn?.term_year_labels
                        ?? {};
                      const selectedTerm = (offer.termMonths && termOptions.includes(offer.termMonths))
                        ? offer.termMonths
                        : termOptions[0];

                      return (
                        <div
                          key={offer.id}
                          className={`p-3 rounded-md border transition-all duration-150 ${isSelected
                            ? 'border-blue-500 bg-blue-50 shadow-sm'
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                          <div className="flex items-start gap-2">
                            <div
                              className="flex-1 min-w-0 cursor-pointer"
                              onClick={() => setSelectedOfferId(offer.id)}
                            >
                              <div className="flex items-center gap-2">
                                {displayLogo ? (
                                  <div className="w-8 h-8 rounded flex items-center justify-center bg-white border">
                                    <img
                                      src={displayLogo}
                                      alt={`${displayName} logo`}
                                      className="max-w-full max-h-6 object-contain"
                                    />
                                  </div>
                                ) : (
                                  <div className="w-8 h-8 rounded flex items-center justify-center bg-gray-100">
                                    <span className="text-xs font-bold text-gray-400">
                                      {displayName.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                )}

                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <div className="font-medium text-sm text-gray-900 truncate">
                                      {displayName}
                                    </div>
                                    <div className="flex items-center gap-1 ml-2">
                                      {offer.isRecommended && (
                                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                      )}
                                      <div className={`w-2 h-2 rounded-full ${offer.status === 'complete' ? 'bg-green-500' :
                                        offer.status === 'draft' ? 'bg-yellow-500' : 'bg-gray-300'
                                        }`}></div>
                                    </div>
                                  </div>
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    <span>{offer.status || 'Draft'}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Contract Term Selector - Inside each offer */}
                          <div className="mt-2 pt-2 border-t border-gray-200" onClick={(e) => e.stopPropagation()}>
                            <Select
                              value={selectedTerm?.toString() || ''}
                              onValueChange={(value) => handleContractTermChange(offer.id, value)}
                            >
                              <SelectTrigger className="w-full h-8 text-xs bg-white border border-gray-300 hover:border-primary/50 transition-colors">
                                <SelectValue>
                                  <div className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3 text-gray-500" />
                                    <span>{formatTermLabel(selectedTerm, offerTermYears)}</span>
                                  </div>
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                {termOptions.map((months) => (
                                  <SelectItem key={months} value={months.toString()}>
                                    {formatTermLabel(months, offerTermYears)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      );
                    }))}
                </div>
              </div>
            </div>

            {/* Enhanced Resizer Handle */}
            <div
              className={`resizer-handle w-2 bg-gray-300 hover:bg-blue-400 cursor-col-resize transition-colors flex items-center justify-center group ${isResizing ? 'bg-blue-500' : ''
                }`}
              onMouseDown={handleMouseDown}
              title="Drag to resize panel"
            >
              <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-0.5 h-4 bg-white rounded-full"></div>
                <div className="w-0.5 h-4 bg-white rounded-full"></div>
              </div>
            </div>

            {/* Detail Panel - Selected Offer */}
            <div className="flex-1 bg-white p-6 overflow-y-auto">
              {selectedOfferId && offers.find(o => o.id === selectedOfferId) ? (
                <OfferManagementCard
                  key={selectedOfferId}
                  offer={offers.find(o => o.id === selectedOfferId)!}
                  index={offers.findIndex(o => o.id === selectedOfferId)}
                  isExpanded={true} // Always expanded in detail view
                  onUpdate={handleUpdateOffer}
                  onRemove={(offerId) => {
                    handleRemoveOffer(offerId);
                    // Select next offer or first offer
                    const remainingOffers = offers.filter(o => o.id !== offerId);
                    if (remainingOffers.length > 0) {
                      setSelectedOfferId(remainingOffers[0].id);
                    } else {
                      setSelectedOfferId(null);
                    }
                  }}
                  onCopy={handleCopyOffer}
                  onToggleExpansion={() => { }} // No-op in detail view
                />
              ) : (
                <div className="h-full flex items-center justify-center rounded-lg border-2 border-dashed border-gray-300">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Plus className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No offer selected
                    </h3>
                    <p className="text-gray-500 mb-4">
                      Select an offer from the list to view and edit details
                    </p>
                    <Button
                      onClick={handleAddOffer}
                      className="bg-primary hover:bg-primary/90 text-white"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Your First Offer
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <WorkflowNavigation
          onNext={handleSaveAndNext}
          onBack={onPrevious}
          isNextDisabled={!isProposalReady || isLoading || isSaving}
          nextButtonText={isSaving ? "Saving..." : isLoading ? "Processing..." : "Continue to Proposal"}
          backButtonText="Back to Documents"
        />
      </div>
    </div>
  );
};

export default OffersStep;
